import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule, FormGroup } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';
import { PaginatedSmartList, ODataSmartList, BaseSmartList } from '../../models/smart-list';

/**
 * CyflareAutocompleteComponent
 *
 * A reusable autocomplete component wrapping PrimeNG `p-autoComplete` with built-in:
 * - HTTP data fetching via SmartList (progressive merge + URL tracking cache)
 * - Org-aware URL rebuilding via `{orgId}` placeholder
 * - Request lifecycle management (stale response detection)
 * - Auto-clear on org/URL change
 * - Reactive form integration
 * - Infinite scroll via native scroll detection on the overlay panel
 * - Support for paginated and OData API response formats
 *
 * Usage:
 * ```html
 * <cyflare-autocomplete
 *   [baseUrl]="'v1/ticketintegration/administrative/organizations/{orgId}/connectwise_boards/'"
 *   [organizationId]="organizationId()"
 *   [formGroup]="boardSelectionForm"
 *   [formFieldName]="'selectedBoard'"
 *   [placeholder]="'Search a board...'"
 *   [fetchOnInit]="true"
 *   (itemSelected)="onBoardSelected($event)"
 * ></cyflare-autocomplete>
 * ```
 */
@Component({
  selector: 'cyflare-autocomplete',
  standalone: true,
  imports: [AutoComplete, ReactiveFormsModule, FormsModule, CommonModule],
  templateUrl: './cyflare-autocomplete.component.html',
  styleUrls: ['./cyflare-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CyflareAutocompleteComponent {
  @ViewChild('autoComplete') autoComplete!: AutoComplete;

  // --- Configuration inputs ---

  /** API endpoint path template. Use `{orgId}` placeholder for org-aware URLs. */
  baseUrl = input.required<string>();

  /** Display field name from API response objects (default: 'name') */
  textField = input<string>('name');

  /** Value field name from API response objects (default: 'id') */
  valueField = input<string>('id');

  /** Query parameter name for search (default: 'q') */
  searchParam = input<string>('q');

  /** Minimum characters before triggering search (default: 0) */
  minChars = input<number>(0);

  /** Debounce delay in ms (default: 300) */
  debounceMs = input<number>(300);

  /** Placeholder text */
  placeholder = input<string>('Search...');

  /** Organization ID — when this changes, the URL is rebuilt and data refetched */
  organizationId = input<number | null>(null);

  /** Whether to show the clear button (default: true) */
  showClear = input<boolean>(true);

  /** Whether to show the dropdown button — mimics combobox behavior (default: false) */
  showDropdown = input<boolean>(false);

  /** Whether to trigger search when input receives focus (default: false) */
  completeOnFocus = input<boolean>(false);

  /** Whether to force user to select from suggestions (default: true) */
  forceSelection = input<boolean>(true);

  /** Whether to fetch data immediately on init/org change (default: true) */
  fetchOnInit = input<boolean>(true);

  /** Pre-selected default item (for edit mode) — full object e.g. {id: 1, name: 'Board A'} */
  defaultItem = input<any>(null);

  /** Whether the input is disabled */
  disabled = input<boolean>(false);

  /** Extra query parameters appended to every request (e.g., 'parent_id=5') */
  extraQueryParams = input<string>('');

  /** FK filter field name — maps to API `relfield` parameter */
  relField = input<string>('');

  /** Comma-separated IDs to force-include in results — maps to API `selected` parameter */
  selectedIds = input<string>('');

  /** Number of results per API request (default: 100) */
  pageSize = input<number>(100);

  /**
   * @deprecated SmartList uses URL tracking instead of TTL cache. Kept for backward compatibility.
   */
  cacheTtlMs = input<number>(0);

  /**
   * @deprecated Use scroll detection instead. Kept for backward compatibility.
   */
  lazyScroll = input<boolean>(false);

  /**
   * @deprecated Use scroll detection instead. Kept for backward compatibility.
   */
  virtualScrollItemSize = input<number>(36);

  /** Use OData response format (default: false, uses Paginated format) */
  useOData = input<boolean>(false);

  // --- Reactive form integration ---

  /** FormGroup to write selected value into */
  formGroup = input<FormGroup | null>(null);

  /** Field name in formGroup for the display text (e.g., 'company_name') */
  formFieldName = input<string>('');

  /** Additional field name in formGroup for the value (e.g., 'company_id') */
  formValueFieldName = input<string>('');

  // --- Outputs ---

  /** Emits the full selected object when user selects an item */
  itemSelected = output<any>();

  /** Emits when the user clears the selection */
  itemCleared = output<void>();

  // --- SmartList data source ---
  smartList: BaseSmartList;

  // --- Internal state ---
  selectedItem = signal<any>(null);
  currentFilter = signal<string>('');

  /** Backward-compatible alias for smartList.items() */
  suggestions = computed(() => this.smartList.items());

  /** Backward-compatible alias for smartList.isLoading() */
  isLoading = computed(() => this.smartList.isLoading());

  private skipNextSearch = false;
  private scrollCleanup: (() => void) | null = null;

  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);

  /** Resolved URL with {orgId} replaced by the actual organization ID */
  resolvedBaseUrl = computed(() => {
    const url = this.baseUrl();
    const orgId = this.organizationId();
    if (orgId !== null && orgId !== undefined) {
      return url.replace('{orgId}', String(orgId));
    }
    return url;
  });

  // Track previous values to detect changes
  private previousOrgId: number | null | undefined = undefined;
  private previousBaseUrl: string | undefined = undefined;
  private initialized = false;

  constructor() {
    // Create SmartList in injection context (HttpClient available)
    this.smartList = new PaginatedSmartList(this.http);

    // React to organizationId and baseUrl changes
    effect(() => {
      const orgId = this.organizationId();
      const url = this.baseUrl();

      // Skip the initial run or detect actual changes
      if (!this.initialized) {
        this.initialized = true;
        this.previousOrgId = orgId;
        this.previousBaseUrl = url;

        // Swap to OData if needed (before any fetch happens)
        if (this.useOData()) {
          this.smartList = new ODataSmartList(this.http);
        }

        // Handle default item on first load
        untracked(() => {
          this.configureSmartList();

          const defaultVal = this.defaultItem();
          if (defaultVal) {
            this.selectedItem.set(defaultVal);
            this.patchForm(defaultVal);
          }

          const needsOrg = this.baseUrl().includes('{orgId}');
          const shouldFetch = this.fetchOnInit() && (!needsOrg || !!orgId);
          this.smartList.setUrl(this.resolvedBaseUrl(), shouldFetch);
        });
        return;
      }

      const orgChanged = orgId !== this.previousOrgId;
      const urlChanged = url !== this.previousBaseUrl;

      if (orgChanged || urlChanged) {
        this.previousOrgId = orgId;
        this.previousBaseUrl = url;

        untracked(() => {
          this.configureSmartList();
          this.currentFilter.set('');

          // Clear input and notify parent if there was a selection
          const hadSelection = this.selectedItem() !== null;
          this.selectedItem.set(null);

          if (hadSelection) {
            this.clearFormFields();
            this.itemCleared.emit();
          }

          const needsOrg = this.baseUrl().includes('{orgId}');
          const shouldFetch = this.fetchOnInit() && (!needsOrg || !!orgId);
          this.smartList.setUrl(this.resolvedBaseUrl(), shouldFetch);
        });
      }
    });

    // React to defaultItem changes (for edit mode population)
    effect(() => {
      const defaultVal = this.defaultItem();
      if (!this.initialized) return;
      untracked(() => {
        this.selectedItem.set(defaultVal);
        if (defaultVal) {
          this.patchForm(defaultVal);
        } else {
          this.clearFormFields();
        }
      });
    });

    // Cleanup on destroy
    this.destroyRef.onDestroy(() => {
      this.detachScrollListener();
      this.smartList.destroy();
    });
  }

  // --- Template event handlers ---

  /** Called by PrimeNG (completeMethod) when user types */
  onSearch(event: { originalEvent: Event; query: string }): void {
    if (this.skipNextSearch) {
      this.skipNextSearch = false;
      return;
    }

    let query = event.query;
    const selectedText = this.getSelectedDisplayText();
    if (selectedText && query === selectedText) {
      query = '';
    }

    this.currentFilter.set(query);
    this.smartList.search(query);
  }

  /** Called by PrimeNG (onSelect) when user selects a suggestion */
  onItemSelected(event: { originalEvent: Event; value: any }): void {
    const item = event.value;
    this.selectedItem.set(item);
    this.patchForm(item, true);
    this.itemSelected.emit(item);
  }

  /** Called by PrimeNG (onClear) when user clicks the clear button */
  onItemCleared(): void {
    this.skipNextSearch = true;
    this.selectedItem.set(null);
    this.clearFormFields(true);
    this.itemCleared.emit();
  }

  /** Called by PrimeNG (onDropdownClick) when user clicks the dropdown button */
  onDropdownClicked(event: { originalEvent: Event; query: string }): void {
    let query = event.query || '';
    const selectedText = this.getSelectedDisplayText();
    if (selectedText && query === selectedText) {
      query = '';
    }

    this.smartList.search(query);
  }

  /** Called when the autocomplete overlay panel is shown */
  onPanelShow(): void {
    setTimeout(() => this.attachScrollListener(), 50);
  }

  /** Called when the autocomplete overlay panel is hidden */
  onPanelHide(): void {
    this.detachScrollListener();
  }

  // --- Public methods ---

  /** Clears the selection, as the clear button does, e.g. when the parent resets its form. */
  public clear(): void {
    this.selectedItem.set(null);
    this.clearFormFields();
    this.itemCleared.emit();
  }

  /** Clears the SmartList cache (items + URL tracking) */
  public clearCache(): void {
    this.smartList.clearCache();
  }

  /** Programmatically trigger a search */
  public fetch(query: string = ''): void {
    this.smartList.search(query);
  }

  // --- Private methods ---

  private configureSmartList(): void {
    const extraParts: string[] = [];
    if (this.relField()) extraParts.push(`relfield=${encodeURIComponent(this.relField())}`);
    if (this.selectedIds()) extraParts.push(`selected=${encodeURIComponent(this.selectedIds())}`);
    if (this.extraQueryParams()) extraParts.push(this.extraQueryParams());

    this.smartList.updateConfig({
      valueField: this.valueField(),
      textField: this.textField(),
      searchParam: this.searchParam(),
      pageSize: this.pageSize(),
      extraParams: extraParts.join('&'),
    });
  }

  private patchForm(item: any, markDirty = false): void {
    const fg = this.formGroup();
    if (!fg) return;

    const fieldName = this.formFieldName();
    const valueFieldName = this.formValueFieldName();
    const tf = this.textField();
    const vf = this.valueField();

    if (fieldName) {
      fg.patchValue({ [fieldName]: item?.[tf] ?? item });
      if (markDirty) fg.get(fieldName)?.markAsDirty();
    }
    if (valueFieldName && item) {
      fg.patchValue({ [valueFieldName]: item[vf] });
      if (markDirty) fg.get(valueFieldName)?.markAsDirty();
    }
  }

  private clearFormFields(markDirty = false): void {
    const fg = this.formGroup();
    if (!fg) return;

    const fieldName = this.formFieldName();
    const valueFieldName = this.formValueFieldName();

    if (fieldName) {
      fg.patchValue({ [fieldName]: null });
      if (markDirty) fg.get(fieldName)?.markAsDirty();
    }
    if (valueFieldName) {
      fg.patchValue({ [valueFieldName]: null });
      if (markDirty) fg.get(valueFieldName)?.markAsDirty();
    }
  }

  private getSelectedDisplayText(): string | null {
    const item = this.selectedItem();
    if (!item) return null;
    return item[this.textField()] ?? null;
  }

  private attachScrollListener(): void {
    this.detachScrollListener();
    const overlay = this.autoComplete?.overlayViewChild?.overlayViewChild?.nativeElement;
    if (!overlay) return;

    const scrollContainer = overlay.querySelector('.p-autocomplete-list-container');
    if (!scrollContainer) return;

    const handler = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        if (this.smartList.hasMore() && !this.smartList.isLoading()) {
          this.smartList.searchMore();
        }
      }
    };

    scrollContainer.addEventListener('scroll', handler);
    this.scrollCleanup = () => scrollContainer.removeEventListener('scroll', handler);
  }

  private detachScrollListener(): void {
    this.scrollCleanup?.();
    this.scrollCleanup = null;
  }
}
